import "./CustomPagination.css";
import PropTypes from "prop-types";

/**
 * Custom pagination component for AG Grid
 *
 * @param {Object} props
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPageChange - Callback when page is changed
 * @param {number} props.totalItems - Total number of items in all pages
 * @param {number} props.pageSize - Number of items per page
 * @returns {React.ReactElement}
 */
const CustomPagination = ({
	currentPage,
	totalPages,
	onPageChange,
	totalItems,
	pageSize,
	onPageSizeChange,
	pageSizeOptions = [10, 25, 50, 100],
}) => {
	// Calculate page numbers to display (show up to 5 pages)
	const getPageNumbers = () => {
		const pages = [];
		const maxPagesToShow = 5;

		if (totalPages <= maxPagesToShow) {
			// Show all pages if there are 5 or fewer
			for (let i = 1; i <= totalPages; i++) {
				pages.push({ type: "page", value: i, key: `page-${i}` });
			}
		} else {
			// Always show first page
			pages.push({ type: "page", value: 1, key: "page-1" });

			// Calculate middle pages
			let startPage = Math.max(2, currentPage - 1);
			let endPage = Math.min(totalPages - 1, currentPage + 1);

			// Adjust if we're near the start or end
			if (currentPage <= 3) {
				endPage = 4;
			} else if (currentPage >= totalPages - 2) {
				startPage = totalPages - 3;
			}

			// Add ellipsis if needed
			if (startPage > 2) {
				pages.push({ type: "ellipsis", value: "...", key: "ellipsis-start" });
			}

			// Add middle pages
			for (let i = startPage; i <= endPage; i++) {
				pages.push({ type: "page", value: i, key: `page-${i}` });
			}

			// Add ellipsis if needed
			if (endPage < totalPages - 1) {
				pages.push({ type: "ellipsis", value: "...", key: "ellipsis-end" });
			}

			// Always show last page
			pages.push({
				type: "page",
				value: totalPages,
				key: `page-${totalPages}`,
			});
		}

		return pages;
	};

	return (
		<div className="frp-grid-pagination">
			{" "}
			<div className="pagination-info">
				<span>
					Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} -{" "}
					{Math.min(currentPage * pageSize, totalItems)} of {totalItems} items
				</span>
				{onPageSizeChange && (
					<select
						className="page-size-selector"
						value={pageSize}
						onChange={(e) => onPageSizeChange(Number(e.target.value))}
						aria-label="Items per page"
					>
						{pageSizeOptions.map((size) => (
							<option key={size} value={size}>
								{size} per page
							</option>
						))}
					</select>
				)}
			</div>
			<div className="pagination-controls">
				<button
					type="button"
					onClick={() => onPageChange(1)}
					disabled={currentPage === 1}
					className="pagination-button first-page"
				>
					&laquo;
				</button>
				<button
					type="button"
					onClick={() => onPageChange(currentPage - 1)}
					disabled={currentPage === 1}
					className="pagination-button prev-page"
				>
					&lsaquo;
				</button>

				{getPageNumbers().map((pageItem) =>
					pageItem.type === "ellipsis" ? (
						<span key={pageItem.key} className="pagination-ellipsis">
							{pageItem.value}
						</span>
					) : (
						<button
							type="button"
							key={pageItem.key}
							onClick={() => onPageChange(pageItem.value)}
							className={`pagination-button page-number ${currentPage === pageItem.value ? "active" : ""}`}
						>
							{pageItem.value}
						</button>
					),
				)}

				<button
					type="button"
					onClick={() => onPageChange(currentPage + 1)}
					disabled={currentPage === totalPages}
					className="pagination-button next-page"
				>
					&rsaquo;
				</button>
				<button
					type="button"
					onClick={() => onPageChange(totalPages)}
					disabled={currentPage === totalPages}
					className="pagination-button last-page"
				>
					&raquo;
				</button>
			</div>
		</div>
	);
};

CustomPagination.propTypes = {
	currentPage: PropTypes.number.isRequired,
	totalPages: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	totalItems: PropTypes.number.isRequired,
	pageSize: PropTypes.number.isRequired,
	onPageSizeChange: PropTypes.func,
	pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
};

export default CustomPagination;
